import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationPrefsDto } from '@rp/contracts';
import { api } from '../lib/api';
import { Card, ScreenTitle, Section, Skeleton, Toggle } from '../components/ui';

/**
 * Уведомления в Telegram.
 *
 * Разбиты на «что происходит прямо сейчас» и «сводки»: это разные по срочности
 * вещи, и в одном списке они мешали друг другу.
 */
export function NotificationsPage() {
  const queryClient = useQueryClient();
  const prefs = useQuery({ queryKey: ['notifications'], queryFn: api.notifications });

  const save = useMutation({
    mutationFn: (patch: Partial<NotificationPrefsDto>) => api.updateNotifications(patch),
    onSuccess: (data) => {
      queryClient.setQueryData(['notifications'], data);
      void queryClient.invalidateQueries({ queryKey: ['checklist'] });
    },
  });

  if (prefs.isLoading || !prefs.data) {
    return (
      <div className="mx-auto max-w-lg space-y-2 px-4 pt-3">
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  const preferences = prefs.data;

  return (
    <div className="mx-auto max-w-lg pb-8">
      <ScreenTitle title="Уведомления" subtitle="Что присылать в Telegram" />

      <Section title="Сразу">
        <Card className="mx-4 p-0">
          <Toggle
            checked={preferences.newReviewAll}
            onChange={(value) => save.mutate({ newReviewAll: value })}
            label="Все новые отзывы"
          />
          <Toggle
            checked={preferences.newReviewNegativeOnly}
            onChange={(value) => save.mutate({ newReviewNegativeOnly: value })}
            label="Только негативные"
            description="Отзывы на 3 звезды и ниже. На 1–2 звезды уведомление приходит даже в тихие часы"
          />
          <Toggle
            checked={preferences.replyFailed}
            onChange={(value) => save.mutate({ replyFailed: value })}
            label="Ошибки публикации"
          />
          <Toggle
            checked={preferences.syncErrors}
            onChange={(value) => save.mutate({ syncErrors: value })}
            label="Сбои сбора отзывов"
          />
        </Card>
      </Section>

      <Section title="Сводки">
        <Card className="mx-4 p-0">
          <Toggle
            checked={preferences.dailyDigest}
            onChange={(value) => save.mutate({ dailyDigest: value })}
            label="Ежедневный дайджест"
          />
          {preferences.dailyDigest && (
            <div className="flex items-center justify-between py-2">
              <span className="t-callout">Время отправки</span>
              <select
                value={preferences.dailyDigestHour}
                onChange={(event) => save.mutate({ dailyDigestHour: Number(event.target.value) })}
                className="min-h-9 rounded-lg bg-[var(--color-surface)] px-2 t-callout text-[var(--color-text)]"
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
          )}
          <Toggle
            checked={preferences.weeklyReport}
            onChange={(value) => save.mutate({ weeklyReport: value })}
            label="Недельный отчёт"
            description="По понедельникам вместе с рекомендациями"
          />
        </Card>
      </Section>

      <Section title="Тишина">
        <Card className="mx-4 p-0">
          <Toggle
            checked={preferences.quietHours !== null}
            onChange={(value) => save.mutate({ quietHours: value ? { from: 22, to: 8 } : null })}
            label="Тихие часы"
            description={
              preferences.quietHours
                ? `С ${preferences.quietHours.from}:00 до ${preferences.quietHours.to}:00 — не беспокоить`
                : 'Не присылать уведомления ночью'
            }
          />
        </Card>
      </Section>
    </div>
  );
}
